import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { Phone, Store } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import { orderDetailQueryOptions } from "@/api/vendure/order/order.query.options";
import { OrderState } from "@/api/vendure/order/order.types";
import { DeliveryTimeRing } from "@/components/business/order-confirm/delivery-time-ring";
import { getDeliveryWindow } from "@/components/business/order-confirm/order-confirm.utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivateVenue } from "@/hooks/useActivateVenue";
import { noindexHead } from "@/lib/seo";
import { orderAtom } from "@/stores/cart.store";

export const Route = createFileRoute("/business/$slug_/order-confirm")({
  ssr: false,
  head: noindexHead,
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : "",
  }),
  component: Sentry.withProfiler(OrderConfirmPage, {
    name: "OrderConfirmPage",
  }),
});

function OrderConfirmPage() {
  useActivateVenue();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { code: codeFromUrl } = Route.useSearch();
  const order = useAtomValue(orderAtom);
  const clearOrder = useSetAtom(orderAtom);

  const orderCode = codeFromUrl || order?.code || "";

  const { data: detail, isLoading } = useQuery(
    orderDetailQueryOptions(orderCode, !!orderCode),
  );

  const { data: venues } = useQuery({
    ...merchantQueryOptions(slug),
    staleTime: Infinity,
  });

  function handleContinue() {
    clearOrder(null);
    navigate({ to: "/business/$slug", params: { slug }, replace: true });
  }

  const seller = detail?.seller;
  const venueImageUrl = venues?.[0]?.image_url;
  const serviceMode = detail?.context.serviceMode;
  const scheduledFor = detail?.scheduledFor ?? order?.scheduledFor;
  const isPickup = serviceMode === "pickup" || serviceMode === "takeaway";

  const rawState = detail?.state ?? order?.state ?? "";
  const state: OrderState = Object.values(OrderState).includes(
    rawState as OrderState,
  )
    ? (rawState as OrderState)
    : OrderState.Pending;

  const { windowStart, windowEnd } = getDeliveryWindow({
    scheduledFor,
    pickupTime: detail?.pickupTime ?? detail?.context.pickupTime,
    orderPlacedAt: detail?.orderPlacedAt,
    serviceMode,
  });

  const isPending = state === OrderState.Pending;

  const title = t(`business.orderConfirm.state.${state}`, {
    defaultValue: t("business.orderConfirm.state.Pending"),
  });

  const ringLabel = isPending
    ? t("business.orderConfirm.ringLabelWaiting")
    : isPickup
      ? t("business.orderConfirm.ringLabelPickup")
      : t("business.orderConfirm.ringLabelDelivery");

  if (isLoading) {
    return (
      <div className="h-dvh bg-background flex flex-col overflow-hidden pt-4 pb-6 px-4 gap-4">
        <div className="flex items-center gap-3 rounded-2xl bg-muted p-4">
          <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <Skeleton className="w-[260px] h-[260px] rounded-full" />
          <div className="flex flex-col items-center gap-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-9 w-20 mt-2" />
            <Skeleton className="h-3 w-36" />
          </div>
        </div>
        <div className="flex justify-center">
          <Skeleton className="h-14 w-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="h-dvh bg-background flex flex-col overflow-hidden pt-4 pb-6"
    >
      {seller && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="mx-4 rounded-2xl bg-muted px-4 py-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-background flex items-center justify-center shrink-0 overflow-hidden">
            {venueImageUrl ? (
              <img
                src={venueImageUrl}
                alt={seller.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Store className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-semibold text-sm text-foreground truncate"
              data-testid="order-confirm.seller-name"
            >
              {seller.name}
            </p>
            <p className="text-xs text-muted-foreground capitalize">
              {serviceMode === "pickup" || serviceMode === "takeaway"
                ? t("business.orderType.pickup")
                : serviceMode === "delivery"
                  ? t("common.delivery.delivery")
                  : ""}
            </p>
          </div>
          {seller.phone && (
            <a
              href={`tel:${seller.phone}`}
              className="w-8 h-8 rounded-full bg-background flex items-center justify-center shrink-0"
            >
              <Phone className="w-4 h-4 text-foreground" />
            </a>
          )}
        </motion.div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-2 gap-4">
        <DeliveryTimeRing
          windowStart={windowStart}
          windowEnd={windowEnd}
          label={ringLabel}
          isPending={isPending}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.35 }}
          className="text-center space-y-1 max-w-xs"
        >
          {(detail?.id ?? order?.id) && (
            <div className="mb-2 flex flex-col items-center gap-0.5">
              <p className="text-2xl font-bold text-foreground">
                #{detail?.id ?? order?.id}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                {detail?.code ?? order?.code}
              </p>
            </div>
          )}
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.35 }}
        className="flex justify-center px-4 pt-2"
      >
        <Button
          data-testid="order-confirm.continue"
          className="rounded-2xl px-10 py-5 text-base"
          onClick={handleContinue}
        >
          {t("business.orderConfirm.hideStatus")}
        </Button>
      </motion.div>
    </motion.div>
  );
}
